import React, { Suspense } from 'react';
import './index.scss';
import {
  Menu,
  Dimmer,
  Loader,
  Button,
  Grid,
} from 'semantic-ui/semantic-ui-imports';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { toPng } from 'html-to-image';
import { unflatten } from 'flat';
import { toast } from 'react-semantic-toasts';
import HeatReclaimCurve from 'components/HeatClaimCurve';
import { PromptToSignInContext } from 'contexts/promptToSignIn';
import StubPlacement from 'components/Results/StubPlacement';
import { getProject } from 'services/projects';
import { useData } from '../../contexts/data';
import AudioCalculations from '../../components/Results/AudioCalculations';
import TechnicalSpecs from '../../components/Results/TechnicalSpecs';
import Calulations from '../../components/Results/Calculations';
import Automatics from '../../components/Results/Automatics';
import DescriptionAndAddons from '../../components/Results/Description';
import RequestOffer from '../../components/Results/ReguestOffer';
import axios from '../../services/request';
import { formatAggregateCalcData } from '../../services/aggregateUtils';
import { Project, Result } from '../../Types/Project';
import { Aggregate } from '../../Types/Aggregates';
import {
  formatAggregateForResults,
  getResult,
  ResultSet,
} from '../../services/results';
import { useAuth } from '../../contexts/auth';
import OfferRequestDetails from '../../components/Results/RequestDetails';
import { getAggregateDetails } from '../../services/aggregate';

const CapacityCurve = React.lazy(
  () => import('../../components/CapacityCurve')
);
const PDFDocument = React.lazy(() => import('../../components/PDFDocument'));

const ResultsPage = () => {
  const { t } = useTranslation();
  const { resultId, menuKey, projectId } = useParams<{
    resultId: string;
    projectId: string;
    menuKey: string;
  }>();
  const { isPartner, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const {
    showSideMenu,
    activeAggregate,
    activeUserInputs,
    setActiveAggregate,
    markAsSaved,
    loadSavedResultData,
    availableAddons,
    fetchAvailableAddons,
  } = useData();
  const [isReady, setIsReady] = React.useState(false);
  const [
    result,
    setResult,
  ] = React.useState<Result>(/* {
    inputs: activeUserInputs,
    data: activeAggregate,
    id: resultId || '',
  } */);

  const [project, setProject] = React.useState<Project>();
  const [formattedData, setFormattedData] = React.useState<ResultSet>();
  const { promptToSignIn } = React.useContext(PromptToSignInContext);

  const [menuKeys, setMenuKeys] = React.useState<string[]>([]);
  const [
    capacityCurveImageUrl,
    setCapacityCurveImageUrl,
  ] = React.useState<string>();
  const [
    heatReclaimCurveImageUrl,
    setHeatReclaimCurveImageUrl,
  ] = React.useState<string | boolean>();
  const capacityCurveRef = React.createRef<HTMLDivElement>();
  const heateReclaimCurveRef = React.createRef<HTMLDivElement>();

  const renderCapacityCurveAsImage = async () => {
    if (capacityCurveRef.current) {
      const url = await toPng(capacityCurveRef.current);
      setCapacityCurveImageUrl(url);
    }
    if (heateReclaimCurveRef.current) {
      if (formattedData?.graphs.heatReclaim?.data) {
        const url = await toPng(heateReclaimCurveRef.current);
        setHeatReclaimCurveImageUrl(url);
      } else {
        setHeatReclaimCurveImageUrl(false);
      }
    }
  };

  React.useEffect(() => {
    if (activeAggregate) {
      setFormattedData(
        formatAggregateForResults(
          activeAggregate,
          t,
          activeUserInputs,
          project,
          result?.name,
          availableAddons,
          result?.alternative_to_name
        )
      );
    }
  }, [activeAggregate, activeUserInputs, result, availableAddons]);

  const calcResults = async () => {
    try {
      const params = formatAggregateCalcData(activeUserInputs);
      const parsedParams = unflatten(params);

      const { data, status } = await axios.put(
        `/aggregates/${activeAggregate?.id}/`,
        parsedParams
      );
      if (status === 200 && data) {
        // TODO: set response data as active aggregate
        setResult({
          ...result,
          id: resultId || '',
          data,
          inputs: activeUserInputs,
        });
      }
    } catch (error) {
      // TODO: handle errors
      toast({
        type: 'error',
        title: t('resultsPage.getResultsError'),
        time: 4000,
        size: 'small',
      });
    }
    setIsReady(true);
  };

  const getDetails = async (
    aggregate: Aggregate | null,
    inputs: any,
    currentResult: Result | undefined,
    isSavedResult = false
  ) => {
    if (aggregate?.id) {
      try {
        const details = await getAggregateDetails(aggregate.id);
        const a = { ...aggregate, ...details };
        setActiveAggregate(a);

        // Fetch available addons for this aggregate/power supply combination
        if (inputs?.power_supply) {
          await fetchAvailableAddons(aggregate.id, inputs.power_supply);
        }

        // If loading a saved result, mark as saved after getting details
        if (isSavedResult) {
          markAsSaved();
        }
        if (!currentResult?.id) {
          setResult({
            ...currentResult,
            id: resultId || '',
            inputs,
            data: a,
          });
          setIsReady(true);
        }
      } catch (error) {
        toast({
          type: 'error',
          title: t('common.somethingWentWrong'),
          time: 4000,
          size: 'small',
        });
      }
    }
  };

  const fetchResults = async (id: string | number) => {
    try {
      const r: Result | null = await getResult(id);
      if (r) {
        // Load data without marking as unsaved
        loadSavedResultData(r.inputs, r.data);
        setResult({ ...r });

        // getDetails will call markAsSaved after enhancing the aggregate
        // Await to ensure availableAddons are fetched before showing results
        await getDetails(r.data, r.inputs, r, true);

        if (r.project) {
          const p = await getProject(r.project);
          if (p) {
            setProject(p);
          }
        }
      }
    } catch (err) {
      toast({
        type: 'error',
        title: t('resultsPage.getResultsError'),
        time: 4000,
        size: 'small',
      });
      navigate('/404');
    }
    setIsReady(true);
  };

  const setKeys = () => {
    const keys = [
      'calculations',
      'description',
      'graphs',
      'audioCalculation',
      'technicalSpecs',
      'placement',
      'automatics',
      'downloadReport',
    ];
    if (activeAggregate?.three_d_model_link) {
      keys.push('3dDrawing');
    }
    // Partners see RequestDetails for quote-related results (by status or result_type)
    const isQuoteFlowResult =
      ['offer_requested', 'quoted', 'declined'].includes(
        result?.status || ''
      ) || ['user_request', 'alternative'].includes(result?.result_type || '');
    if (isPartner && isQuoteFlowResult) {
      keys.push('requestOfferDetails');
    } else if (!isPartner) {
      keys.push('requestOffer');
    }
    setMenuKeys(keys);
  };

  const getData = async () => {
    if (resultId) {
      fetchResults(resultId);
    } else {
      setResult({
        ...result,
        id: result?.id || resultId || '',
        inputs: activeUserInputs,
        data: activeAggregate,
      });
      calcResults();
    }
  };

  const get3dDrawing = async () => {
    window.open(activeAggregate?.three_d_model_link, '_blank');
  };

  const renderGraphs = () => (
    <>
      {!!formattedData?.graphs.capacity.descriptionList?.length && (
        <>
          <div className="capacity-curve">
            <CapacityCurve dataset={formattedData?.graphs.capacity.data} />
          </div>
          <ul>
            {formattedData?.graphs.capacity.descriptionList.map(
              (text: string, idx: number) => (
                <li key={idx}>
                  <p>{text}</p>
                </li>
              )
            )}
          </ul>
        </>
      )}
      {formattedData?.graphs.heatReclaim?.data && (
        <>
          <br></br>
          <br></br>
          <h3>{formattedData?.graphs.heatReclaim.title}</h3>
          <div className="heat-reclaim-curve">
            <HeatReclaimCurve
              dataset={formattedData?.graphs.heatReclaim.data}
              eff={activeAggregate?.system_data.heating.heat_reclaim_curve.eff}
            />
          </div>
          {!!formattedData?.graphs.heatReclaim.descriptionList?.length && (
            <ul>
              {formattedData?.graphs.heatReclaim.descriptionList.map(
                (text: string, idx: number) => (
                  <li key={idx}>
                    <p>{text}</p>
                  </li>
                )
              )}
            </ul>
          )}
        </>
      )}
    </>
  );

  React.useEffect(() => {
    if (resultId) {
      getData();
    } else {
      getDetails(activeAggregate, activeUserInputs, result);
    }
  }, [resultId]);

  React.useEffect(() => {
    setKeys();
  }, [result, isPartner]);

  React.useEffect(() => {
    renderCapacityCurveAsImage();
  }, [formattedData, capacityCurveRef, heateReclaimCurveRef]);

  return isReady ? (
    <div className="results-page">
      <div
        className={`results-page__sidebar ${
          showSideMenu ? 'results-page__sidebar--open' : ''
        }`}
      >
        <Menu vertical size="small">
          {menuKeys.map((key) => (
            <Menu.Item
              active={menuKey === key}
              as="a"
              key={key}
              onClick={() => {
                if (
                  !isAuthenticated &&
                  ['requestOffer', 'downloadReport'].includes(key)
                ) {
                  promptToSignIn(window.location.pathname);
                } else if (projectId && resultId) {
                  navigate(`/projects/${projectId}/results/${resultId}/${key}`);
                } else {
                  navigate(`/results/${key}`);
                }
              }}
            >
              {t(`resultsPage.${key}`)}
            </Menu.Item>
          ))}
        </Menu>
      </div>

      <div
        className={`results-page__inner ${
          showSideMenu ? 'results-page__inner--open' : ''
        } ${
          menuKey === 'downloadReport' ? 'results-page__inner--no-scroll' : ''
        }`}
      >
        {activeAggregate?.name && (
          <Grid className="results-page__inner__header">
            <Grid.Row>
              <div>
                <h4>
                  {`${activeAggregate.name}${
                    project?.name ? ` - ${project.name}` : ''
                  }${result?.name ? ` - ${result.name}` : ''}`}
                </h4>
                {result?.alternative_to_name && (
                  <p className="results-page__alternative-to">
                    {t('resultList.alternativeTo', {
                      name: result.alternative_to_name,
                    })}
                  </p>
                )}
              </div>
            </Grid.Row>
          </Grid>
        )}
        {(menuKey === 'calculations' || !menuKey) && (
          <Calulations data={formattedData} />
        )}
        {menuKey === 'description' && <DescriptionAndAddons />}
        {menuKey === 'graphs' && (
          <>
            <h2>{formattedData?.graphs.title}</h2>
            <h3>{formattedData?.graphs.capacity.title}</h3>
            <Suspense fallback={<Loader />}>{renderGraphs()}</Suspense>
          </>
        )}
        {menuKey === 'audioCalculation' && (
          <>
            {/* <AudioCalculations data={formattedData?.audioCalc} /> */}
            <AudioCalculations data={formattedData?.audioCalcSummer} />
          </>
        )}
        {menuKey === 'technicalSpecs' && (
          <TechnicalSpecs data={formattedData} />
        )}
        {menuKey === 'placement' && <StubPlacement />}
        {menuKey === 'automatics' && <Automatics />}
        {menuKey === '3dDrawing' && (
          <div>
            <Menu.Item>
              <Button color="blue" onClick={get3dDrawing}>
                {t('resultsPage.3dDrawingPage.openButton')}
              </Button>
            </Menu.Item>
          </div>
        )}
        {menuKey === 'downloadReport' && !!activeAggregate && (
          <div>
            {capacityCurveImageUrl?.length &&
              ((typeof heatReclaimCurveImageUrl === 'string' &&
                heatReclaimCurveImageUrl.length) ||
                heatReclaimCurveImageUrl === false) && (
                <Suspense fallback={<Loader active={true} />}>
                  <PDFDocument
                    data={formattedData}
                    aggregate={activeAggregate}
                    capacityCurveImageUrl={capacityCurveImageUrl}
                    heatReclaimCurveImageUrl={heatReclaimCurveImageUrl || ''}
                  />
                </Suspense>
              )}
            {(!capacityCurveImageUrl ||
              heatReclaimCurveImageUrl === undefined) && (
              <Loader active={true} />
            )}
            <div className="report-graphs">
              <div
                ref={capacityCurveRef}
                className="report-graphs__capacity-curve-pdf"
              >
                {formattedData?.graphs?.capacity?.data && (
                  <CapacityCurve
                    dataset={formattedData?.graphs.capacity.data}
                  />
                )}
              </div>
              <div
                ref={heateReclaimCurveRef}
                className="report-graphs__heat-reclaim-curve-pdf"
              >
                {formattedData?.graphs?.heatReclaim?.data && (
                  <HeatReclaimCurve
                    dataset={formattedData?.graphs.heatReclaim.data}
                    eff={
                      activeAggregate?.system_data?.heating?.heat_reclaim_curve
                        ?.eff
                    }
                  />
                )}
              </div>
            </div>
          </div>
        )}
        {menuKey === 'requestOffer' && !isPartner && (
          <RequestOffer initialProject={project} />
        )}
        {menuKey === 'requestOfferDetails' && !!isPartner && result && (
          <OfferRequestDetails
            name={result?.name}
            alternativeToName={result?.alternative_to_name}
            message={result?.notes}
            userId={result?.offer_requested_by}
          />
        )}
      </div>
    </div>
  ) : (
    <div className="result-overlay">
      <Dimmer inverted active page>
        <Loader size="large">{t('common.loading')}</Loader>
      </Dimmer>
    </div>
  );
};

export default ResultsPage;
